import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from '../hooks/useAppState';
import type { Theme } from '../types';
import { Button } from '../components/ui/Button';
import { ThemeToggle } from '../components/ThemeToggle';
import { Input } from '../components/ui/Input';
import { Dialog } from '../components/ui/Dialog';
import {
  Settings as SettingsIcon,
  Palette,
  Database,
  Download,
  Upload,
  Trash2,
  AlertTriangle,
  Globe,
  CheckCircle,
} from 'lucide-react';

interface SettingsPageProps {
  theme: Theme;
  onThemeChange: (theme: Theme) => void;
}

export const SettingsPage: React.FC<SettingsPageProps> = ({ theme, onThemeChange }) => {
  const { decks, settings, updateSettings, exportData, importData, clearAllData } = useAppState();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'general' | 'data'>('general');
  const [importText, setImportText] = useState('');
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [notification, setNotification] = useState<string | null>(null);

  const tabs = [
    { id: 'general' as const, label: 'General', icon: SettingsIcon },
    { id: 'data' as const, label: 'Data', icon: Database },
  ];

  const showNotification = (message: string) => {
    setNotification(message);
    setTimeout(() => setNotification(null), 3000);
  };

  const handleExport = () => {
    const data = exportData();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flashai-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    showNotification('Data exported successfully');
  };

  const handleImport = () => {
    if (!importText.trim()) return;
    const result = importData(importText);
    if (result.success) {
      showNotification('Data imported successfully');
      setImportText('');
    } else {
      showNotification(result.message);
    }
  };

  const handleClearData = () => {
    clearAllData();
    setShowClearConfirm(false);
    showNotification('All data cleared');
    navigate('/');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <SettingsIcon className="w-6 h-6" />
          Settings
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Manage your app preferences and data.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 pb-3 pt-1 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Notification */}
      {notification && (
        <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg text-sm">
          <CheckCircle className="w-4 h-4" />
          {notification}
        </div>
      )}

      {/* General Settings */}
      {activeTab === 'general' && (
        <div className="space-y-8 max-w-2xl">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Appearance</h2>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Theme</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Choose your preferred color theme</p>
              </div>
              <ThemeToggle theme={theme} onChange={onThemeChange} variant="dropdown" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-green-600 dark:text-green-400" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Language</h2>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Default language for card generation</p>
            <select
              value={settings.language}
              onChange={(e) => updateSettings({ language: e.target.value })}
              className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100"
            >
              {[
                { value: 'en', label: 'English' },
                { value: 'es', label: 'Spanish' },
                { value: 'fr', label: 'French' },
                { value: 'de', label: 'German' },
                { value: 'it', label: 'Italian' },
                { value: 'ja', label: 'Japanese' },
                { value: 'zh', label: 'Chinese' },
                { value: 'ko', label: 'Korean' },
              ].map((lang) => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Data Management */}
      {activeTab === 'data' && (
        <div className="space-y-8 max-w-2xl">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Backup & Restore</h2>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Export your data to a JSON file or import from a backup.</p>
            <div className="flex gap-3">
              <Button onClick={handleExport} leftIcon={<Download className="w-4 h-4" />}>
                Export Data
              </Button>
              <label className="relative inline-flex cursor-pointer">
                <input
                  type="file"
                  accept=".json"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (ev) => setImportText(ev.target?.result as string);
                      reader.readAsText(file);
                    }
                  }}
                  className="hidden"
                />
                <span className="inline-flex items-center justify-center gap-2 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm font-medium px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <Upload className="w-4 h-4" />
                  Import from File
                </span>
              </label>
            </div>
            {importText && (
              <div className="space-y-2">
                <textarea
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  className="w-full h-32 px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-xs font-mono text-gray-900 dark:text-gray-100"
                  placeholder="Paste your exported JSON data here..."
                />
                <Button onClick={handleImport} size="sm">Import Data</Button>
              </div>
            )}
          </div>

          <div className="bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800 p-6 space-y-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <h2 className="text-lg font-semibold text-red-900 dark:text-red-300">Danger Zone</h2>
            </div>
            <p className="text-sm text-red-700 dark:text-red-400">These actions cannot be undone. Please be careful.</p>
            <Button
              variant="danger"
              leftIcon={<Trash2 className="w-4 h-4" />}
              onClick={() => setShowClearConfirm(true)}
            >
              Clear All Data
            </Button>
          </div>
        </div>
      )}

      {/* Clear Confirmation Dialog */}
      <Dialog
        open={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        title="Clear All Data"
        maxWidth="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
            <p className="text-sm text-red-800 dark:text-red-300">
              Are you sure you want to delete everything? This action cannot be undone.{' '}
              <strong>
                ({decks.length} decks, {decks.reduce((acc, d) => acc + d.cards.length, 0)} cards will be lost)
              </strong>
            </p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="ghost" onClick={() => setShowClearConfirm(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleClearData}>
              Yes, Clear Everything
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
